import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateTripTypeRequestSchema,
  type CreateTripTypeRequest,
  type TripTypeDetail,
  type TripTypeListItem,
  UpdateTripTypeRequestSchema,
  type UpdateTripTypeRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateTripType, useUpdateTripType } from '../use-trip-types';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface TripTypeFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  tripType?: TripTypeDetail | TripTypeListItem | null;
}

const CREATE_DEFAULTS: CreateTripTypeRequest = {
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 */
const TripTypeFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. FIT"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive trip types are hidden from the lead-form filter and user-form destination picker. Existing destinations carrying this trip type stay intact."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const TripTypeFormDrawer = ({
  open,
  onOpenChange,
  mode,
  tripType,
}: TripTypeFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createTripType = useCreateTripType();
  const updateTripType = useUpdateTripType();
  const pending = createTripType.isPending || updateTripType.isPending;

  const createForm = useForm<CreateTripTypeRequest>({
    resolver: zodResolver(CreateTripTypeRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateTripTypeRequest>({
    resolver: zodResolver(UpdateTripTypeRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (tripType) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: tripType.name,
        description: tripType.description ?? '',
        sortOrder: tripType.sortOrder,
        isActive: tripType.isActive,
      });
    }
  }, [open, isCreate, tripType, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createTripType.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!tripType) return;
    await updateTripType.mutateAsync({
      id: tripType.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <TripTypeFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add trip type' : `Edit ${tripType?.name ?? 'trip type'}`}
      description="The name is what agents see. The machine code is generated automatically."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
